import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { createContext, use, useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react'

import { actingSiteEndedReload, onActingSiteEnded, showSite } from '@/shared/api/acting-site-ended'
import { createApiClient } from '@/shared/api/client'
import { clearFailedCalls, setFailedCallsOwner } from '@/shared/api/failed-calls'
import { recordSessionEnd, sessionEnd } from '@/shared/api/session-end'
import { onUnauthorized } from '@/shared/api/unauthorized'
import { useRuntimeConfig } from '@/shared/config/runtime-config-context'

import { AuthStateProvider, useAuth, type AuthState } from './auth-state'
import { clearFreshSignIn } from './fresh-sign-in'
import { IdleSessionWarning } from './IdleSessionWarning'
import { rememberReturnPath } from './return-path'
import {
  createSessionClient,
  type SessionClient,
  type SessionIdleTime,
  type SignedInSession,
  type SignInAnswer,
} from './session-client'
import { announceSessionChange, listenForSessionChanges, pageReload } from './session-sync'
import { SignInRenewalRequiredError } from './sign-in-hop'
import { clearSignedInStorage } from './signed-in-storage'

/** Whether this site's session is known yet, and whether there is one. */
export type SessionStatus = 'checking' | 'signedIn' | 'signedOut'

/** The session Site Management holds for the signed-in person on this site. */
export interface SessionState {
  /** `checking` until the first answer; `signedOut` whenever nobody is signed in to B2C. */
  readonly status: SessionStatus
  /** Who is signed in, or `null`. */
  readonly session: SignedInSession | null
  /** Starts the session after B2C, or sends a new code while one is awaited. A refusal rejects with an `ApiError`. */
  readonly start: () => Promise<SignInAnswer>
  /** Finishes the sign-in with the emailed code. A refusal rejects with an `ApiError`. */
  readonly confirmCode: (code: string) => Promise<SignInAnswer>
}

interface Known {
  readonly session: SignedInSession | null
  /** The 401s heard while this session was held; each asks the API whether it still holds it. */
  readonly unauthorized: number
}

const SessionContext = createContext<SessionState | null>(null)

/** Reads the session. Throws outside `AuthProvider`. */
export function useSession(): SessionState {
  const state = use(SessionContext)
  if (state === null) {
    throw new Error('useSession must be used inside an AuthProvider.')
  }
  return state
}

/**
 * Keeps the session that follows a B2C sign-in: reads it once signed in, starts it from the auth
 * callback, and ends it on sign-out. `client` replaces the API calls, for tests.
 */
export function SessionProvider(props: { readonly client?: SessionClient | undefined; readonly children: ReactNode }) {
  const auth = useAuth()
  const { apiBaseUrl } = useRuntimeConfig()

  const { client: given } = props
  const { getAccessToken } = auth
  const client = useMemo(
    () =>
      given
      ?? createSessionClient(
        createApiClient(apiBaseUrl, getAccessToken),
        // The idle check runs on its own, so it never starts a B2C sign-in: one that needs it fails.
        createApiClient(apiBaseUrl, (options) => getAccessToken({ ...options, isSilent: true })),
      ),
    [given, apiBaseUrl, getAccessToken],
  )

  const [known, setKnown] = useState<Known | null>(null)
  const isSignedIn = !auth.isResolving && auth.isAuthenticated

  // Signed out, as by another tab sharing the sign-in: forget the session, so the next sign-in,
  // perhaps someone else's, reads its own.
  if (!auth.isResolving && !auth.isAuthenticated && known !== null) {
    setKnown(null)
  }

  useEffect(() => {
    if (!isSignedIn || known !== null) {
      return
    }

    const controller = new AbortController()
    const check = async () => {
      let session: SignedInSession | null
      try {
        session = await client.current(controller.signal)
      } catch {
        // Unreadable counts as none: the auth callback starts a new one and shows any refusal.
        session = null
      }
      if (!controller.signal.aborted) {
        if (session !== null) {
          clearFreshSignIn()
        }
        setKnown({ session, unauthorized: 0 })
      }
    }
    void check()
    return () => controller.abort()
  }, [client, isSignedIn, known])

  // A 401 once signed in may mean the API no longer knows the session, such as after its idle
  // time. Ask again, keeping the page meanwhile: dropping it would load the page again into the
  // same call, so a 401 the session survives would repeat for good. One the API dropped sends the
  // person back through the callback for a new one. Asked silently, as a background 401 may come
  // with nobody at the page: a sign-in that cannot renew without leaving it keeps the session
  // shown, to be renewed by the person's next action.
  useEffect(
    () =>
      onUnauthorized(() => {
        setKnown((previous) =>
          previous === null || previous.session === null
            ? previous
            : { ...previous, unauthorized: previous.unauthorized + 1 },
        )
      }),
    [],
  )

  const unauthorized = known?.unauthorized ?? 0
  useEffect(() => {
    if (!isSignedIn || unauthorized === 0) {
      return undefined
    }

    const controller = new AbortController()
    const recheck = async () => {
      let session: SignedInSession | null
      try {
        session = await client.current(controller.signal, { isSilent: true })
      } catch (error: unknown) {
        if (error instanceof SignInRenewalRequiredError || error instanceof InteractionRequiredAuthError) {
          return
        }
        session = null
      }
      if (!controller.signal.aborted && session === null) {
        setKnown({ session: null, unauthorized: 0 })
      }
    }
    void recheck()
    return () => controller.abort()
  }, [client, isSignedIn, unauthorized])

  // Another tab changed who acts in the shared session, as by a new sign-in or an impersonation:
  // this one loads afresh, so it never shows one person while acting as another.
  useEffect(
    () =>
      listenForSessionChanges(() => {
        clearSignedInStorage()
        pageReload.now()
      }),
    [],
  )

  // The API put a Super User's session back on its own site (110552): the app loads afresh at home,
  // and this site's other tabs too, so none shows the other site's records while working on this one.
  useEffect(
    () =>
      onActingSiteEnded(() => {
        clearSignedInStorage()
        announceSessionChange()
        actingSiteEndedReload.now()
      }),
    [],
  )

  const remember = useCallback((answer: SignInAnswer) => {
    if (answer.status === 'signedIn') {
      // Before the session shows, so the callback sends the person where a sign-in link goes (SM-39).
      if (answer.destination !== undefined && answer.destination !== null) {
        rememberReturnPath(answer.destination)
      }
      clearFreshSignIn()
      setKnown({ session: answer.session, unauthorized: 0 })
      announceSessionChange()
    }
    return answer
  }, [])

  const start = useCallback(async () => remember(await client.start()), [client, remember])
  const confirmCode = useCallback(
    async (code: string) => remember(await client.confirmCode(code)),
    [client, remember],
  )

  const status: SessionStatus = auth.isResolving
    ? 'checking'
    : !auth.isAuthenticated
      ? 'signedOut'
      : known === null
        ? 'checking'
        : known.session === null
          ? 'signedOut'
          : 'signedIn'

  const session = status === 'signedIn' ? (known?.session ?? null) : null
  // Report a problem sends the last failed calls: another person, or nobody, never gets these (111162).
  const realPerson = session === null ? null : (session.impersonatorUserProfileId ?? session.userProfileId)
  // Before the page's own effects, so a request a page starts as the session arrives counts as this person's.
  useLayoutEffect(() => {
    if (status !== 'checking') {
      setFailedCallsOwner(realPerson)
    }
  }, [status, realPerson])
  // Sent with each request, so a write the session would run on another site is refused (110582).
  const siteShown = session === null ? null : (session.actingSiteId ?? session.siteId)
  useLayoutEffect(() => {
    if (status !== 'checking') {
      showSite(siteShown)
    }
  }, [status, siteShown])
  const state = useMemo<SessionState>(() => ({ status, session, start, confirmCode }), [status, session, start, confirmCode])

  // Sign-out ends the session first; failing to reach the API must not keep anyone signed in. The
  // session is not read again here: B2C's sign-out leaves the page, and if it cannot start, the
  // person is still shown signed in, to be told so and try again.
  const { signOut: signOutOfB2C, mode } = auth
  const signOut = useCallback(async (options?: { readonly isSessionEnded?: boolean }) => {
    // First, before anything that could fail or leave the page.
    clearSignedInStorage()
    clearFailedCalls()
    if (mode === 'b2c' && options?.isSessionEnded !== true) {
      try {
        await client.end()
      } catch {
        // The session expires on its own.
      }
    }
    await signOutOfB2C()
  }, [client, mode, signOutOfB2C])
  // Signed in to B2C is not signed in: nobody is shown as signed in until the site's session starts.
  // The name stored in TMS wins; the token's name is only for a profile with none.
  const signedInUser = status === 'signedIn' ? auth.user : null
  const storedName = session === null ? '' : fullName(session)
  const user = useMemo(
    () => (signedInUser === null || storedName === '' ? signedInUser : { displayName: storedName }),
    [signedInUser, storedName],
  )
  const authState = useMemo<AuthState>(() => ({ ...auth, user, signOut }), [auth, user, signOut])

  // Reading the session is a request like any other, so it restarts the session's idle time. One
  // already gone is left to the warning, which signs out as at the idle end.
  const keepAlive = useCallback(
    async (): Promise<'alive' | 'ended'> => ((await client.current()) === null ? 'ended' : 'alive'),
    [client],
  )

  // Asks the API how long the session has left without renewing it, as another tab may have kept it
  // alive unheard: alive, its end moves on; ended, the caller signs out.
  const checkEnd = useCallback(async (): Promise<'alive' | 'ended'> => {
    const askedAt = Date.now()
    let idleTime: SessionIdleTime | null
    try {
      idleTime = await client.idleTimeLeft()
    } catch (error: unknown) {
      // A site that signs in through the sign-in site cannot ask without renewing its sign-in, which
      // leaves the page: past the end it last heard of, the session has ended; before it, it holds.
      if (error instanceof SignInRenewalRequiredError) {
        const end = sessionEnd()
        return end !== null && askedAt >= end ? 'ended' : 'alive'
      }
      throw error
    }
    if (idleTime === null || idleTime.secondsLeft <= 0) {
      return 'ended'
    }
    recordSessionEnd({
      observedAt: askedAt,
      end: askedAt + idleTime.secondsLeft * 1000,
      idleMs: idleTime.idleTimeoutSeconds * 1000,
    })
    return 'alive'
  }, [client])

  return (
    <AuthStateProvider value={authState}>
      <SessionContext value={state}>
        {props.children}
        {/* Kept through MSAL's sign-out, which reads as resolving, so a failed one is still shown. */}
        {mode === 'b2c' && auth.isAuthenticated && known !== null && known.session !== null && (
          <IdleSessionWarning onStay={keepAlive} onCheck={checkEnd} onSignOut={signOut} />
        )}
      </SessionContext>
    </AuthStateProvider>
  )
}

/** The session's first and last name, or blank when TMS holds neither. */
function fullName(session: SignedInSession): string {
  return [session.firstName, session.lastName]
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(' ')
}
